import * as api from "@/api";
import LocalStorage from "@/classes/local-storage";
import { getNewNoteTemplate } from "@/data/new-note-template";
import shortcutColorSets from "@/data/shortcut-color-sets";
import { globals } from "@/globals";
import * as signals from "@/signals";
import {
	BookmarkInfo,
	DialogInfo,
	Divider,
	FolderInfo,
	GeneralSettingsInfo,
	MenuInfo,
	MenuItemInfo,
	MenuItemParamInfo,
	NoteInfo,
	PromptInfo,
	SearchEngineInfo,
	TnInfo,
} from "@/types";
import i18next, { t } from "i18next";
import { CSSProperties, JSX } from "react";
import { renderToString } from "react-dom/server";
import { LongPressCallback } from "use-long-press";

export async function blurInput(): Promise<void> {
	signals.isClosingSearchSuggestions.value = true;
	signals.isSearchFocused.value = false;
	// Cannot unfocus wallpaper here, or the secondary screen's wallpaper will
	// be unfocused
	if (signals.generalSettings.value.shouldAutoClearSearchBar) {
		signals.searchInputValue.value = "";
	}
	await sleep(globals.ANIMATION_WAIT_TIME);
	signals.isSearchSuggestionsShown.value = false;
	signals.isClosingSearchSuggestions.value = false;
}

export function clearSearchHistory(): void {
	signals.searchHistory.value = [];
	LocalStorage.saveSearchHistory([]);
}

export async function closeMenu(): Promise<void> {
	signals.isClosingMenu.value = true;
	await sleep(globals.ANIMATION_WAIT_TIME);
	signals.isClosingMenu.value = false;
	signals.menu.value = null;
}

export function closePopup(key: string): void {
	signals.arePopupsShown.value = {
		...signals.arePopupsShown.value,
		[key]: false,
	};
	for (const value of Object.values(signals.arePopupsShown.value)) {
		if (value) {
			return;
		}
	}
}

export async function copyText(text: string): Promise<void> {
	await navigator.clipboard.writeText(text);
	signals.greetingText.value = t("copiedToClipboard") + " ￣ω￣=";
}

export function deepCopy<T>(value: T): T {
	return JSON.parse(JSON.stringify(value)) as T;
}

export function downloadExtension(): void {
	const queryParams = encodeData({
		action: "start",
	});
	if (i18next.language === "en-US") {
		window.open("https://intro.limestart.page/?" + queryParams);
	} else {
		window.open("https://intro.limestart.cn/?" + queryParams);
	}
}

export function encodeData(
	data: Record<string, string | null | undefined>,
): string {
	const array: string[] = [];
	for (const key in data) {
		const value = data[key];
		if (!value) {
			continue;
		}
		array.push(key + "=" + encodeURIComponent(value));
	}
	return array.join("&");
}

export function getColorUrl(colorSet = getRandomColorSet()): string {
	return `color://${colorSet.backgroundColor}&${colorSet.color}`;
}

export function getDisplayName(): string | null {
	const login = signals.login.value;
	return (
		login.name ||
		login.email?.split("@")[0] ||
		login.phone ||
		login.username ||
		null
	);
}

export function getNumberKeyFromCode(code: string): number | null {
	const number = parseInt(code.replace(/Digit|Numpad/gi, "")) - 1;
	if (isNaN(number)) {
		return null;
	} else if (number < 0) {
		return 9;
	}
	return number;
}

export function getRandomArrayItem<T>(array: readonly T[]): T {
	return array[Math.floor(Math.random() * array.length)];
}

export function getRandomColorSet(): CSSProperties {
	const randomColorSet = getRandomArrayItem(shortcutColorSets);
	return {
		backgroundColor: randomColorSet.background,
		color: randomColorSet.foreground,
	};
}

export function getRandomInteger(min: number, max: number): number {
	return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function getRedirectLink(redirectId: string): string {
	return globals.BACKEND + "/counter/v1/redirect/" + redirectId;
}

export function getShortcutTip(
	key: string,
	modifier: {
		altKey?: boolean;
	},
): string {
	let tip = "";
	if (modifier.altKey) {
		tip += globals.isMac ? "⌥ " : "Alt+";
	}
	tip += key;
	return tip;
}

export function getSupportLink(): string {
	return (
		"https://support.retiehe.com/?" +
		encodeData({
			app: globals.APP_NAME.toLowerCase(),
			username: signals.login.value.username,
		})
	);
}

export function getTranslatorUrl(searchEngineId: string): string {
	const translators = {
		"preset-baidu": "https://fanyi.baidu.com/#en/zh/",
		"preset-bing": "https://www.bing.com/translator?text=",
		"preset-google": "https://translate.google.com/#auto/zh-CN/",
	};
	if (searchEngineId in translators) {
		return translators[searchEngineId as keyof typeof translators];
	} else {
		return translators["preset-baidu"];
	}
}

export function handleKeyboardClick(
	onClick: () => void,
): (event: KeyboardEvent | React.KeyboardEvent<Element>) => void {
	return (event: KeyboardEvent | React.KeyboardEvent<Element>): void => {
		switch (event.key) {
			case "Enter":
			case " ": {
				onClick();
				event.preventDefault();
				break;
			}
			default:
				break;
		}
	};
}

export function handleLongPress(
	onContextMenu: (event: MouseEvent) => void,
): LongPressCallback {
	return (event) => {
		if (!globals.isIos) {
			return;
		}
		const touchEvent = event as unknown as TouchEvent;
		const mouseEvent = new MouseEvent("contextmenu", {
			clientX: touchEvent.touches[0].clientX,
			clientY: touchEvent.touches[0].clientY,
		});
		onContextMenu(mouseEvent);
	};
}

export function isDivider(item: MenuItemInfo | Divider): item is Divider {
	return (item as Divider).divider !== undefined;
}

export function isFolder(item: BookmarkInfo | FolderInfo): item is FolderInfo {
	return item.itemType === "folder";
}

export function logIn(): void {
	openPopup("login");
}

export function openEditShortcut(options: {
	bookmark: BookmarkInfo;
	event?: React.MouseEvent<Element>;
}): void {
	const { bookmark, event } = options;
	if (!signals.login.value.username) {
		logIn();
		return;
	}
	signals.editingShortcut.value = bookmark;
	signals.editShortcutPopup.value = {
		isShown: true,
		position: {
			left: (event ? event.clientX : window.innerWidth / 2) - 150,
			top: (event ? event.clientY : window.innerHeight / 2) - 260,
		},
	};
}

export function openNote(note?: NoteInfo): void {
	if (!note) {
		note = getNewNoteTemplate();
	}
	if (!signals.login.value.username) {
		logIn();
		return;
	}
	signals.currentNote.value = note;
	openPopup("notes");
}

export function openPopup(key: string): void {
	signals.arePopupsShown.value = {
		...signals.arePopupsShown.value,
		[key]: true,
	};
	signals.isSearchFocused.value = false;
	signals.isSearchSuggestionsShown.value = false;
	if (!signals.isSecondaryScreenShown.value) {
		signals.isWallpaperFocused.value = false;
	}
}

export function openWebPage(
	url: string,
	options?: {
		shouldSendReferrer?: boolean;
	},
): void {
	if (!url.startsWith("http")) {
		url = "http://" + url;
	}
	if (!options?.shouldSendReferrer) {
		const newLink = document.createElement("a");
		newLink.href = url;
		newLink.rel = "noopener noreferrer";
		if (signals.generalSettings.value.shouldOpenInNewPage) {
			newLink.target = "_blank";
		} else {
			newLink.target = "_top";
		}
		newLink.click();
	} else if (signals.generalSettings.value.shouldOpenInNewPage) {
		window.open(url);
	} else {
		(top || window).location.href = url;
	}
}

export function pushToSearchHistory(newValue: string): void {
	if (!newValue) {
		return;
	}
	signals.searchHistory.value = [
		...signals.searchHistory.value.filter((item) => item !== newValue),
		newValue,
	];
	if (signals.searchHistory.value.length >= 5) {
		signals.searchHistory.value = signals.searchHistory.value.slice(-5);
	}
	LocalStorage.saveSearchHistory(signals.searchHistory.value);
}

export function search(text: string): void {
	let searchEngineUrl = signals.selectedSearchEngine.value.url;
	const isBaidu =
		searchEngineUrl.includes("//www.baidu.com/s?") ||
		searchEngineUrl.includes("//m.baidu.com/s?") ||
		searchEngineUrl.includes("//baidu.com/s?");
	if (isBaidu) {
		searchEngineUrl =
			"https://" +
			(globals.isMobile && window.innerWidth <= 600 ? "m" : "www") +
			".baidu.com/s?" +
			encodeData({
				tn: signals.tnInfo.value?.tn,
				// eslint-disable-next-line sort-keys
				ch: signals.tnInfo.value?.ch,
				ie: "utf-8",
				word: text,
			});
	} else if (searchEngineUrl.includes("%s")) {
		searchEngineUrl = searchEngineUrl.replace(
			"%s",
			encodeURIComponent(text),
		);
	} else {
		searchEngineUrl += encodeURIComponent(text);
	}
	if (signals.generalSettings.value.isSearchHistoryEnabled) {
		pushToSearchHistory(text);
	}
	if (!signals.selectedSearchEngine.value.id.startsWith("custom")) {
		void api.recordSearchEngineUsage(signals.selectedSearchEngine.value.id);
	}
	openWebPage(searchEngineUrl);
	void blurInput();
	signals.isWallpaperFocused.value = false;
}

export async function showDialog(
	text: string | JSX.Element,
	options: DialogInfo = {},
	callbacks?: typeof globals.dialogCallbacks,
): Promise<void> {
	return new Promise<void>((resolve) => {
		if (callbacks) {
			globals.dialogCallbacks = callbacks;
		}
		if (!globals.dialogCallbacks.ok) {
			globals.dialogCallbacks.ok = (): void => {
				resolve();
			};
		}
		if (typeof text === "string") {
			options.text = text;
		} else {
			options.html = renderToString(text);
		}
		signals.dialog.value = options;
	});
}

export function showMenu(
	event: MouseEvent | React.MouseEvent<HTMLElement>,
	menuItems: MenuItemParamInfo[],
	options = {} as {
		isModal?: boolean;
		shouldBeLighterInDarkTheme?: boolean;
		x?: number;
		y?: number;
	},
): void {
	const newMenuInfo: MenuInfo = {
		isModal: options.isModal ?? true,
		items: [],
		shouldBeLighterInDarkTheme: options.shouldBeLighterInDarkTheme || false,
		x: options.x || event.clientX || 0,
		y: options.y || event.clientY || 0,
	};
	globals.menuCallbacks = {};
	for (const item of menuItems) {
		if (!item.when) {
			continue;
		}
		newMenuInfo.items.push(item.content);
		if (!isDivider(item.content) && item.onClick) {
			globals.menuCallbacks[item.content.id || item.content.text] =
				item.onClick;
		}
	}
	signals.menu.value = newMenuInfo;
}

export function showPrompt(
	label: string,
	options: PromptInfo = {},
): Promise<string> {
	return new Promise<string>((resolve) => {
		globals.promptCallback = resolve;
		options.label = label;
		signals.prompt.value = options;
	});
}

export function sleep(delay: number): Promise<void> {
	return new Promise((resolve) => {
		window.setTimeout(resolve, delay);
	});
}

export async function toggleSecondaryScreen(): Promise<void> {
	if (signals.isSecondaryScreenShown.value) {
		signals.isClosingSecondaryScreen.value = true;
		signals.isWallpaperFocused.value = false;
		await sleep(globals.ANIMATION_WAIT_TIME);
		signals.isSearchFocused.value = false;
		signals.isSecondaryScreenShown.value = false;
		signals.isClosingSecondaryScreen.value = false;
	} else {
		signals.isClosingSearchBar.value = true;
		signals.isSearchFocused.value = false;
		signals.isSecondaryScreenShown.value = true;
		signals.isWallpaperFocused.value = true;
		void blurInput();
		await sleep(globals.ANIMATION_WAIT_TIME);
		signals.isClosingSearchBar.value = false;
	}
}

export async function uninstallServiceWorker(): Promise<void> {
	if (!navigator.serviceWorker) {
		return;
	}
	const registrations = await navigator.serviceWorker.getRegistrations();
	for (const registration of registrations) {
		await registration.unregister();
	}
	console.info("Service workers uninstalled.");
}

export function updateSelectedSearchEngine(options: {
	currentSearchEngineId: string;
	newSearchEngine: SearchEngineInfo;
}): void {
	const { currentSearchEngineId, newSearchEngine } = options;
	LocalStorage.saveLastSearchEngineId(currentSearchEngineId);
	signals.selectedSearchEngine.value = newSearchEngine;
	LocalStorage.saveSelectedSearchEngineId(newSearchEngine.id);
}

export async function updateSettings(
	newValues: Partial<GeneralSettingsInfo>,
): Promise<void> {
	if (Object.keys(newValues).length === 0) {
		return;
	}
	signals.generalSettings.value = {
		...signals.generalSettings.value,
		...newValues,
	};
	LocalStorage.updateGeneralSettings(newValues);
	if (!signals.login.value.username) {
		return;
	}
	try {
		await api.uploadSettings(newValues);
	} catch (error) {
		void api.handleApiError(error);
	}
}

export function updateTnInfo(newValue: TnInfo): void {
	signals.tnInfo.value = newValue;
	if (newValue.tn) {
		localStorage.setItem("tn", newValue.tn);
	} else {
		localStorage.removeItem("tn");
	}
	if (newValue.ch) {
		localStorage.setItem("ch", newValue.ch);
	} else {
		localStorage.removeItem("ch");
	}
}
